/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Geodata } from '../components/cloud-processing/geodata';
import { GeodataUpload } from '../components/cloud-processing/geodata-upload';
import { SfmViewer } from '../components/cloud-processing/gcp-page/sfm-viewer';
import { IntegrationListContent } from '../components/integrations/integration-list/integration-list-content';
import { IntegrationsState } from '../components/integrations/state/integrations-state';
import { ProjectEmail } from '../components/project/email/project-email';
import { Overview } from '../components/project/overview/overview';
import { ProjectTransferRequest } from '../components/project-transfer-request/project-transfer-request';
import { ProjectSettings } from '../components/project-settings/project-settings';
import { ProjectUsers } from '../components/project/users/project-users';
import { FileManagerPage } from '../pages/file-manager/file-manager-page';
import { TimelineList } from '../components/timeline-list';
import { CompanyStateProvider, useCompanyState } from '../state/company-state';
import { ProjectArtifactsProvider } from '../state/project-artifacts-state';
import { useProjectAccess } from '../state/project-access-state';
import { useAppContext } from '../state/app-state';
import { ProjectContext, ProjectState } from '../state/project-state/project-state';
import { ProjectTimelineProvider } from '../state/project-timeline-state';
import { ProjectReferenceProvider } from '../state/project-reference-state';
import { useProjectsLayoutContext } from './projects';

const ProjectLayout = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedProject } = useProjectsLayoutContext();
  const { currentUser } = useAppContext();
  const { t } = useTranslation('components');
  const { setProjectAccess } = useProjectAccess();

  // Navigation state set by UnifiedProjectList when clicking a project row.
  const companyId = (location.state as { companyId?: string } | null)?.companyId;

  // Pre-compute per-project role from user model without waiting for the project API response.
  // companyusers contains the company IDs the user is a company admin for.
  React.useEffect(() => {
    if (!currentUser) {
      return;
    }
    if (currentUser.isOrganizationAdmin) {
      setProjectAccess({ isAdmin: true, isOrgAdmin: true });
    } else if (companyId) {
      const isCompanyAdmin = (currentUser.companyusers ?? []).includes(companyId);
      setProjectAccess({ isAdmin: isCompanyAdmin, isOrgAdmin: false });
    } else if ((currentUser.companyusers ?? []).length === 0) {
      // No company memberships — definitely a project user in every project.
      setProjectAccess({ isAdmin: false, isOrgAdmin: false });
    }
    // If companyId is unknown and user has companyusers entries, leave projectAccess as
    // undefined until ProjectLayoutContent resolves it from the project API response.
  }, [currentUser, companyId, setProjectAccess]);

  React.useEffect(() => {
    if (!projectId) {
      setSelectedProject(undefined);
      void navigate('/projects', { replace: true });
    }
  }, [navigate, projectId, setSelectedProject]);

  React.useEffect(
    () => () => {
      setSelectedProject(undefined);
    },
    [setSelectedProject],
  );

  if (!projectId) {
    return null;
  }

  return (
    <ProjectState
      loadingMode="page-content"
      message={t('droneLoader.loadingProject', { ns: 'components' })}
    >
      <ProjectReferenceProvider>
        <ProjectArtifactsProvider>
          <ProjectTimelineProvider>
            <CompanyStateProvider>
              <IntegrationsState key={projectId}>
                <ProjectLayoutContent />
              </IntegrationsState>
            </CompanyStateProvider>
          </ProjectTimelineProvider>
        </ProjectArtifactsProvider>
      </ProjectReferenceProvider>
    </ProjectState>
  );
};

const ProjectLayoutContent = () => {
  const { project, isAdmin, isOrganizationAdmin } = React.useContext(ProjectContext);
  const { setSelectedProject } = useProjectsLayoutContext();
  const { setProjectAccess } = useProjectAccess();
  const { company, fetchCompany, reset: resetCompany } = useCompanyState();
  const projectIdentifier = project?.id;
  const projectName = project?.name ?? '';

  React.useEffect(() => {
    if (projectIdentifier) {
      setSelectedProject({
        id: projectIdentifier,
        name: projectName,
      });
    }
  }, [projectIdentifier, projectName, setSelectedProject]);

  React.useEffect(() => {
    if (projectIdentifier) {
      setProjectAccess({
        isAdmin: isAdmin(),
        isOrgAdmin: isOrganizationAdmin(),
      });
    }
    return () => {
      setProjectAccess(undefined);
    };
  }, [projectIdentifier, setProjectAccess, isAdmin, isOrganizationAdmin]);

  React.useEffect(() => {
    const companyId = project?.companyId ?? null;
    if (companyId && isAdmin()) {
      void fetchCompany(companyId);
    } else {
      resetCompany();
    }
    return () => {
      resetCompany();
    };
  }, [project?.companyId, isAdmin, fetchCompany, resetCompany]);

  React.useEffect(() => {
    if (company && projectIdentifier) {
      const hasPortalLicense = company.licenses.some(
        (l) => l.type === 'PORTAL' || l.type === 'PORTAL_DEMO',
      );
      setProjectAccess({
        isAdmin: isAdmin(),
        isOrgAdmin: isOrganizationAdmin(),
        isDesignLicenseOnly: company.licenses.length > 0 && !hasPortalLicense,
      });
    }
  }, [company, projectIdentifier, setProjectAccess, isAdmin, isOrganizationAdmin]);

  if (!project) {
    return null;
  }

  return <Outlet />;
};

const ProjectOverviewRoute = () => {
  return <Overview showTitle={false} />;
};

const ProjectEmailRoute = () => {
  return <ProjectEmail />;
};

const ProjectSettingsRoute = () => {
  return <ProjectSettings />;
};

const ProjectUsersRoute = () => {
  return <ProjectUsers />;
};

const ProjectTimelineRoute = () => {
  return <TimelineList />;
};

const ProjectTransferRequestRoute = () => {
  return <ProjectTransferRequest />;
};

const ProjectFileManagerRoute = () => {
  return <FileManagerPage />;
};

export {
  ProjectEmailRoute,
  ProjectFileManagerRoute,
  ProjectGeodataGcpRoute,
  ProjectGeodataRoute,
  ProjectGeodataUploadRoute,
  ProjectIntegrationsRoute,
  ProjectLayout,
  ProjectOverviewRoute,
  ProjectSettingsRoute,
  ProjectTimelineRoute,
  ProjectTransferRequestRoute,
  ProjectUsersRoute,
};

const ProjectGeodataRoute = () => {
  return <Geodata />;
};

const ProjectGeodataGcpRoute = () => {
  const { geodataId } = useParams();

  return <SfmViewer geodataId={geodataId} />;
};

const ProjectGeodataUploadRoute = () => {
  return <GeodataUpload />;
};

const ProjectIntegrationsRoute = () => {
  return <IntegrationListContent />;
};
